import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { LazyImage } from 'common/react/components/LazyImage';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { selectCardHandler } from './util';
import { useGetUserAuthenticated } from 'common/react/hooks/use-get-user-authenticated';
import { MediaThumbnailFooter } from './MediaThumbnailFooter';

export const MediaThumbnailVideo = ({ video, index }) => {
    const videoRef = useRef();

    const { userAuthenticated } = useGetUserAuthenticated();

    const videoData = PULSE.app.common.content.getContentModel(
        video,
        PULSE.app.common.CONSTANTS.VARIANT_CONFIGURATIONS.THUMBNAIL_MEDIA
    );
    const publishLabel =
        PULSE.app.common.content.getPublishStringFromTag(video);

    const videoImage =
        PULSE.app.common.content.getImageFromContentItem(videoData);

    // Responsive
    const windowSize = useWindowSize();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.phablet;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const videoLink = useMemo(() => {
        if (video.hotlinkUrl) {
            return video.hotlinkUrl;
        }

        if (videoData) {
            const videoUrlParams = {
                limit: 50,
                tagNames: `ProgramCategory:${videoData.tags
                    .find((item) => item.label.includes('ProgramCategory:'))
                    ?.label.replace('ProgramCategory:', '')}`,
                references: ['AFL_COMPSEASON', 'AFL_ROUND']
                    .map((ref) => {
                        const refValue = videoData.references.find(
                            (item) => item.type === ref
                        );
                        if (refValue) {
                            return `${ref}:${refValue.id}`;
                        }
                        return null;
                    })
                    .filter((ref) => !!ref)
                    .join(',')
            };

            return `${videoData.link}&${PULSE.core.url.buildQueryString(
                videoUrlParams
            )}`;
        }

        return '';
    }, [video, videoData]);

    const isPremium = PULSE.app.common.content.isPremium(video);

    const videoModalData = {
        contentId: videoData.id,
        mediaId: videoData.mediaId,
        mediaTitle: PULSE.app.templating.helper.cleanString(videoData.title),
        mediaDescription: PULSE.app.templating.helper.cleanString(
            videoData.description
        ),
        subtitle: PULSE.app.templating.helper.cleanString(videoData.subtitle),
        type: videoData.type,
        link: videoLink,
        publishFrom: videoData.publishFrom + 1,
        mediaDate: PULSE.app.templating.helper.getDateNice(
            videoData.publishFrom
        )
    };

    useEffect(() => {
        if (
            videoRef.current &&
            userAuthenticated !== null &&
            !video.hotlinkUrl
        ) {
            if (isPremium && !userAuthenticated) {
                PULSE.app.UpsellModalContainer?.createInstance(
                    videoRef.current
                );
            } else {
                PULSE.app.VideoPlayerModal?.createInstance(videoRef.current);
            }
            videoRef.current.dataset.modalAdded = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAuthenticated, videoRef.current, video]);

    // if video is premium, wait for auth result before render
    if (isPremium && userAuthenticated === null) {
        return;
    }

    // eslint-disable-next-line consistent-return
    return (
        <article
            className={`media-thumbnail ${
                publishLabel?.label?.length
                    ? `media-thumbnail--has-publish-label media-thumbnail--${publishLabel.cssClass}`
                    : ''
            }${isPremium ? ' media-thumbnail--premium' : ''}`}
        >
            <div className="media-thumbnail__link">
                <header
                    className={`media-thumbnail__image-container placeholder placeholder--small placeholder--dark`}
                >
                    <LazyImage
                        image={videoImage}
                        mediaQueryConfig={
                            PULSE.app.common.CONSTANTS.VARIANT_CONFIGURATIONS
                                .THUMBNAIL_MEDIA
                        }
                        cssClass="js-faded-image fade-in-on-load object-fit-cover-picture__img is-loaded"
                    />

                    <div className={`media-thumbnail__video-duration`}>
                        <SvgIcon
                            icon={'video-play-small'}
                            className={`icon media-thumbnail__duration-icon`}
                        />
                        <span className={`media-thumbnail__duration-text`}>
                            {PULSE.app.common.date.durationToTime(
                                videoData.duration
                            )}
                        </span>
                    </div>

                    {publishLabel?.label?.length > 0 && (
                        <div
                            className={`media-thumbnail__content-label-container media-thumbnail__content-label-container--header ${
                                publishLabel.cssClass || ''
                            }`}
                        >
                            <span className="media-thumbnail__content-label">
                                {publishLabel.label}
                            </span>
                            <SvgIcon
                                icon={`content-label-edge`}
                                className={`icon media-thumbnail__content-label-container-edge`}
                            />
                        </div>
                    )}
                </header>
                <div
                    className={`media-thumbnail__caption ${
                        publishLabel?.label?.length > 0
                            ? 'media-thumbnail__caption--has-label'
                            : ''
                    }`}
                >
                    {publishLabel?.label?.length > 0 && (
                        <div className="media-thumbnail__content-label-container media-thumbnail__content-label-container--caption">
                            <span className="media-thumbnail__content-label">
                                {publishLabel.label}
                            </span>
                            <SvgIcon
                                icon={`content-label-edge`}
                                className={`icon media-thumbnail__content-label-container-edge`}
                            />
                        </div>
                    )}
                    {video.hotlinkUrl ? (
                        <a
                            ref={videoRef}
                            className={`media-thumbnail__headline-link js-media-thumbnail__link`}
                            href={videoLink}
                            data-index-position={index + 1}
                            onClick={(evt) =>
                                selectCardHandler(evt, videoData?.title)
                            }
                            data-content-type={
                                PULSE.app.common.CONSTANTS.TRACKING
                                    .CONTENT_TYPES.VIDEO
                            }
                        >
                            <h2 className={`media-thumbnail__title`}>
                                {videoData.title}
                            </h2>
                        </a>
                    ) : (
                        <a
                            ref={videoRef}
                            className={`media-thumbnail__headline-link js-media-thumbnail__link`}
                            data-ui-modal={`${
                                isPremium && !userAuthenticated
                                    ? '#UpsellModal'
                                    : '#VideoModal'
                            }`}
                            href={videoLink}
                            data-content-id={videoData.id}
                            data-ui-args={JSON.stringify(videoModalData)}
                            data-index-position={index + 1}
                            onClick={(evt) =>
                                selectCardHandler(evt, videoData?.title)
                            }
                            data-content-type={
                                PULSE.app.common.CONSTANTS.TRACKING
                                    .CONTENT_TYPES.VIDEO
                            }
                        >
                            <h2 className={`media-thumbnail__title`}>
                                {videoData.title}
                            </h2>
                        </a>
                    )}
                    <p className={`media-thumbnail__description`}>
                        {videoData?.description}
                    </p>
                    {!showResponsiveItems ? (
                        <MediaThumbnailFooter data={video} />
                    ) : null}
                </div>
            </div>
            {showResponsiveItems ? <MediaThumbnailFooter data={video} /> : null}
        </article>
    );
};

MediaThumbnailVideo.propTypes = {
    video: PropTypes.object,
    index: PropTypes.number,
    tags: PropTypes.string
};
